import { Activity } from "@/types/activity";

/**
 * Utility functions for activity resource lookups.
 * Designed to work with both:
 * 1. Current: Client-side lookups (agents/docs arrays passed in)
 * 2. Future: Server-provided data (activity.resource_data)
 */

export interface Agent {
  id?: string;
  uuid?: string;
  agent_id?: string;
  elevenlabs_id?: string;
  elevenlabs_agent_id?: string;
  name?: string;
  title?: string;
  branding?: {
    primaryColor?: string;
    secondaryColor?: string;
    logoUrl?: string;
    customAvatarUrl?: string;
    gradientEnabled?: boolean;
    gradientAngle?: number;
  };
  brand_color?: string;
}

export interface Document {
  id?: string;
  name?: string;
}

export interface Conversation {
  id: string;
  [key: string]: any;
}

/**
 * Find agent by matching multiple ID/name fields
 * Future-proof: will use activity.resource_data if available
 */
export function findAgent(
  activity: Activity,
  agents: Agent[] = []
): Agent | null {
  // Future: Check if backend already provided the data
  if ((activity as any).resource_data?.agent) {
    return (activity as any).resource_data.agent;
  }

  if (!agents?.length) return null;

  const lookupId =
    activity.resource_type === "genie" || activity.category === "genie"
      ? activity.resource_id
      : activity.resource_type === "conversation"
        ? activity.metadata?.genie_id || activity.metadata?.agent_id
        : null;

  const lookupName =
    activity.resource_type === "genie" || activity.category === "genie"
      ? activity.resource_name
      : activity.resource_type === "conversation"
        ? activity.metadata?.genie_name || activity.metadata?.agent_name
        : null;

  return agents.find(
    (a) =>
      (lookupId &&
        (a.id === lookupId ||
          a.uuid === lookupId ||
          a.agent_id === lookupId ||
          a.elevenlabs_id === lookupId ||
          a.elevenlabs_agent_id === lookupId)) ||
      (lookupName && (a.name === lookupName || a.title === lookupName))
  ) || null;
}

/**
 * Find document by ID or name
 * Future-proof: will use activity.resource_data if available
 */
export function findDocument(
  activity: Activity,
  documents: Document[] = []
): Document | null {
  // Future: Check if backend already provided the data
  if ((activity as any).resource_data?.document) {
    return (activity as any).resource_data.document;
  }

  if (!documents?.length || activity.resource_type !== "document")
    return null;

  return (
    documents.find(
      (d) => d.id === activity.resource_id || d.name === activity.resource_name
    ) || null
  );
}

/**
 * Create minimal agent object for display when full data unavailable
 */
export function createMinimalAgent(activity: Activity): Agent {
  return {
    id: activity.resource_id,
    elevenlabs_id: activity.resource_id,
    name: activity.resource_name || "Unknown Agent",
  };
}

/**
 * Create minimal conversation object for display
 */
export function createMinimalConversation(activity: Activity): Conversation {
  return {
    id: activity.resource_id || "",
  };
}
